import Auth from '@aetosky/auth';
import BaseApi from './BaseApi'
import axios from 'axios'
import config from '@/config';
const CancelToken = axios.CancelToken

class Api extends BaseApi {
  /** Overide base methods **/

  /**
   * Normal http request
   *
   * @param {string} method http method
   * @param {string} url http url
   * @param {Object} data params for get or body data for post, put, patch
   * @param {Object} config axios config object. See https://github.com/mzabriskie/axios
   */
  submit(method, url, data, config = {}) {
    return super.submit(method, url, data, config)
  }

  /**
   * Submit http request with Authorization header
   *
   * @param {string} method http method
   * @param {string} url http url
   * @param {Object} data params for get or body data for post, put, patch
   * @param {Object} config axios config object. See https://github.com/mzabriskie/axios
   */
  async authSubmit(method, url, data, config = {}) {
    const accesstoken = await Auth.getInstance().getToken()
    super.setAuthToken(accesstoken)
    return super.submit(method, url, data, config)
  }

  /** Api methods **/
  getUploadLimits() {
    return this.authSubmit('get', '/upload_limits')
  }

  getNotifications() {
    return this.authSubmit('get', `/notifications`)
  }

  readNotification(id) {
    return this.authSubmit('put', `/notifications/${id}`, {
      status: 1
    })
  }

  seeNotification(id) {
    return this.authSubmit('put', `/notifications`)
  }

  readAllNotification(id) {
    return this.authSubmit('put', `/notifications`, {
      status: 1
    })
  }

  getWorkspaceImages(workspaceId) {
    return this.authSubmit('get', `/workspaces/${workspaceId}/imageries`)
  }

  moveToFolder(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/folders/move/image`,
      {
        image_id: form.imageId,
        folder_id: form.folderId ? form.folderId : undefined
      }
    )
  }

  convertToImage(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.resultId}/rastertoimage`,
      {
        name: form.name,
        folder_id: form.folderId ? form.folderId : null
      }
    )
  }

  timeSerialInspect(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/inspect`,
      form.data
    )
  }

  copyTo(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/copy_to_workspace`,
      form.data
    )
  }

  copyVectorTo(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/vectors/copy_to_workspace`,
      form.data
    )
  }

  copyModelTo(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/models/copy_to_workspace`,
      form.data
    )
  }

  multiMoveToFolder(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/move`,
      {
        image_ids: form.imageIds,
        folder_id: form.folderId ? form.folderId : undefined
      }
    )
  }

  getSharedModels(payload) {
    return this.authSubmit('get', 'models/shared', null, {
      params: {
        search: payload.search || null,
        folder_id: payload.folderId || null,
        page: payload.page || null,
        perpage: payload.perPage || null,
        order_by: payload.orderBy,
        order: payload.order
      }
    })
  }

  spatialAdjustment(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/spatial_adjustment`,
      form.data
    )
  }
  createTerrainTile(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/create_terrain_tile`,
      form.data
    )
  }
  changeWorkspaceOwner(form) {
    return this.authSubmit('post', `/admin/workspaces/move`, form)
  }
  getCommunityModels() {
    return this.authSubmit('get', `community/models`)
  }

  getSubmittedCommunity() {
    return this.authSubmit('get', `community/models/submited`)
  }

  removeModel(id) {
    return this.authSubmit('delete', `community/models/${id}`)
  }

  getSharedImages(param) {
    return this.authSubmit('get', `imageries/shared`, param)
  }

  updateModel(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/models/${form.modelId}`,
      {
        categories: form.categories ? form.categories : undefined,
        name: form.name,
        description: form.description,
        thumbnail: form.thumbnail,
        image_ids: form.image_ids,
        allow_ssr_usage: form.allow_ssr_usage
      }
    )
  }

  submitModel(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/models/${form.modelId}/submit`,
      {
        additional_data: form.additionalData,
        name: form.name,
        description: form.description,
        avatar: form.avatar,
        background: form.background,
        image_ids: form.images,
        sku: form.sku,
        version: form.version
      }
    )
  }

  updateSubmittedModel(form) {
    return this.authSubmit('put', `/community/models/${form.id}`, {
      additional_data: form.additionalData,
      name: form.name,
      description: form.description,
      avatar: form.avatar,
      background: form.background,
      image_ids: form.images,
      model_id: form.modelId,
      version: form.version
    })
  }

  makeCopy(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/models/shared/make_copy`,
      {
        name: form.name,
        model_id: form.modelId
      }
    )
  }

  onMakeCopyImage(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/shared/make_copy`,
      {
        image_id: form.imageId,
        workspace_id: form.workspaceId,
        name: form.name
      }
    )
  }

  removeShare(form) {
    return this.authSubmit('delete', `/models/shared`, form)
  }

  removeShareImage(form) {
    return this.authSubmit('delete', `/imageries/shared`, {
      image_id: form.imageId
    })
  }

  moveToModelFolder(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/models/move`,
      {
        model_id: form.modelId,
        folder_id: form.folderId ? form.folderId : undefined
      }
    )
  }

  onRenameFolder(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/folders/${form.folderId}`,
      { name: form.name }
    )
  }

  onRenameModelFolder(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/model_folders/${form.folderId}`,
      { name: form.name }
    )
  }

  // getFolderImages (workspaceId) {
  //   return this.authSubmit('get', `/workspaces/${workspaceId}/folders`)
  // }

  getDataFolder(form) {
    let param = {
      id: form.folderId
    }
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/folders`,
      param
    )
  }

  getDataFolderV2(form) {
    let params = {
      search: form.search,
      id: form.folderId,
      page: form.page,
      order_by: form.orderBy,
      order: form.order
    }
    for (let property in params) {
      if (!params[property]) delete params[property]
    }
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/folders/v2`,
      params
    )
  }

  getDataModelMiniFolder(form) {
    let param = {
      id: form.folderId
    }
    for (let property in param) {
      if (!param[property]) delete param[property]
    }
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/model_folders`,
      param
    )
  }

  getDataModelFolder(form) {
    let param = {
      search: form.search,
      id: form.folderId,
      order_by: form.orderBy,
      order: form.order,
      page: form.page
    }
    for (let property in param) {
      if (!param[property]) delete param[property]
    }
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/model_folders/v2`,
      param
    )
  }

  createFolder(form) {
    return this.authSubmit('post', `/workspaces/${form.workspaceId}/folders`, {
      name: form.name,
      parent_id: form.parentId ? form.parentId : undefined
    })
  }

  createModelFolder(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/model_folders`,
      {
        name: form.name,
        parent_id: form.parentId ? form.parentId : undefined
      }
    )
  }

  updateWorkspaceImage(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}`,
      form.data
    )
  }

  multiDeleteWorkspaceImage(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/imageries`,
      {
        imagery_ids: form.imageIds
      }
    )
  }

  runARDTool(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/tools?group=${form.group}&type=${form.code}`,
      form.data
    )
  }

  uploadGEOImage(request) {
    return this.authSubmit(
      'post',
      `/workspaces/${request.form.id}/imageries/client_upload`,
      request.form.imagedata,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 360 * 60 * 1000,
        onUploadProgress: request.progressCallback,
        cancelToken: new CancelToken(request.cancelCallback)
      }
    )
  }

  // createBingImage (form) {
  //   return this.authSubmit('post', `/workspaces/${form.id}/imageries/upload?type=bing`, form.data_img)
  // }

  createImageFromAOI(form) {
    form.filters.start_date = form.filters.fromDate
    form.filters.end_date = form.filters.toDate
    form.filters.task_name = form.filters.name
    form.filters.folder_id = form.filters.folderId
    form.filters.url = form.filters.url ? form.filters.url : undefined
    let needProxy = [''].includes(form.type)
    if (needProxy) {
      /* return this.authSubmit('post', `/v1/proxyapi/workspaces/${form.id}/imageries/upload?type=${form.type}`, form.filters) */
    } else {
      return this.authSubmit(
        'post',
        `/workspaces/${form.id}/imageries/upload?type=${form.type}`,
        form.filters
      )
    }
  }

  getWorkspaces(data) {
    return this.authSubmit('get', `/workspaces`, data)
  }

  createWorkspace(form) {
    return this.authSubmit('post', `/workspaces`, form)
  }

  shareWorkspace(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/share`,
      form.data
    )
  }

  onShare(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/${form.type}/share`,
      form.data
    )
  }

  removeSharedUser(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/${form.type}/share/user`,
      form.data
    )
  }

  detailFolder(form) {
    return this.authSubmit(
      'get',
      `workspaces/${form.workspaceId}/${form.type}/${form.folderId}`
    )
  }

  updateWorkspace(form) {
    return this.authSubmit('put', `/workspaces/${form.id}`, {
      name: form.name,
      categories: form.tags
    })
  }

  deleteWorkspace(id) {
    return this.authSubmit('delete', `/workspaces/${id}`)
  }

  detailWorkspace(id) {
    return this.authSubmit('get', `/workspaces/${id}`)
  }

  getNewResults(data) {
    let param = {
      q: data.search ? data.search : '',
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      from_date: data.fromDate ? data.fromDate : '',
      to_date: data.toDate ? data.toDate : '',
      order_by:
        data.sortBy && data.sortBy.length > 0 ? data.sortBy[0] : 'created_at',
      order: data.sortDesc ? 'desc' : 'asc'
    }
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/results`,
      param
    )
  }

  getDetailResult(data) {
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/results/${data.resultId}`
    )
  }

  deleteDetectionResult(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/results/${form.id}`
    )
  }

  renameDetectionResult(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/results/${form.id}`,
      form.data
    )
  }

  rectifyDetectionResult(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.id}/rectify`,
      form.data
    )
  }

  getCentroidResult(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.id}/centroid`,
      {}
    )
  }

  downloadDetectionResult(url) {
    return this.authSubmit('get', url, {
      timeout: 60 * 60 * 1000
    })
  }

  morphology(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.resultId}/morphology`,
      form.data
    )
  }

  polygonize(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.resultId}/polygonize`,
      form.data
    )
  }

  vectorization(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.resultId}/vectorization`,
      form.data
    )
  }

  vectorizationDataset(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/datasets/${form.resultId}/vectorize`,
      form.data
    )
  }

  createNewLayerResult(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results`,
      form.data
    )
  }

  getModels(workspaceId, filter) {
    return this.authSubmit('get', `/workspaces/${workspaceId}/models`, filter)
  }

  getENV(workspaceId) {
    return this.authSubmit('get', `/workspaces/${workspaceId}/env`)
  }


  EditLayerResult(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/results/${form.data.ref_id}`,
      form.data
    )
  }

  download(url) {
    return this.authSubmit('get', url)
  }

  getTasks(workspaceId, data) {
    let param = {
      total: data && data.total ? data.total : 10,
      from_date: data && data.from_date ? data.from_date : '',
      to_date: data && data.to_date ? data.to_date : '',
      sort_by:
        data && data.sortBy && data.sortBy.length > 0
          ? data.sortBy[0]
          : 'created_at',
      sort_desc: data && data.sortDesc ? data.sortDesc : true,
      status: data && data.status ? data.status : []
    }
    return this.authSubmit('get', `/workspaces/${workspaceId}/tasks`, param)
  }

  reCallApi(form) {
    return this.authSubmit(form.method, form.url, form.payload)
  }

  onRestartTask(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/tasks/${form.taskId}/resubmit`
    )
  }

  getTaskChange(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/tasks/${form.taskID}`
    )
  }

  cancelTask(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/tasks/${form.taskId}/cancel`
    )
  }

  nvdi(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/${form.id}/nvdi`,
      form.data
    )
  }

  getImageriesType(data) {
    return this.authSubmit('get', `/imageries/type`, data)
  }

  addAOI(workspaceId, form) {
    return this.authSubmit('post', `/workspaces/${workspaceId}/aois`, form)
  }

  updateAOI(workspaceId, form) {
    return this.authSubmit(
      'put',
      `/workspaces/${workspaceId}/aois/${form.id}`,
      form
    )
  }

  deleteAOI(workspaceId, form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/aois/${form.id}`
    )
  }

  getAOIs(workspaceId) {
    return this.authSubmit('get', `/workspaces/${workspaceId}/aois`)
  }

  getTrainType() {
    return this.authSubmit('get', `/models/type`)
  }

  deleteModel(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/models/${form.modelId}`
    )
  }

  prediction(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/predict`,
      payload
    )
  }

  checkBalanceTokenPredict(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/predict/cost`,
      payload
    )
  }

  checkBalanceTokenARD(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/imageries/ard/cost`,
      payload
    )
  }

  communityPrediction(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/community/predict`,
      payload
    )
  }

  trainingModel(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/models/train`,
      payload
    )
  }
  uploadModel(workspaceId, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/models/upload`,
      payload
    )
  }
  getImage(payload) {
    return this.authSubmit('post', `/imagery/discovery`, payload)
  }

  getOtherImages(payload) {
    return this.authSubmit('post', `/image_source/discovery`, payload)
  }

  clipImage(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/clip`,
      form.data
    )
  }

  clipImageWithoutTask(form) {
    return this.authSubmit(
      'post',
      `v1/workspaces/${form.workspaceId}/imageries/${form.imageId}/clip_without_task`,
      form.data
    )
  }

  getSources() {
    return this.authSubmit('get', `/image_source`)
  }

  createSource(form) {
    return this.authSubmit('post', `/image_source`, form)
  }

  deleteSource(id) {
    return this.authSubmit('delete', `/image_source/${id}`)
  }

  /* deadTreeDetection (form) {
    return this.authSubmit('get', `/v1/workspaces/${form.workspaceId}/imageries/${form.id}/deadtree`)
  } */

  calculateStretch(form) {
    return this.authSubmit(
      'post',
      `tile/v2/imageries/${form.ownerId}/${form.workspaceId}/${form.imgId}/quantile/estimate`,
      form.data,
      {
        timeout: 60 * 60 * 1000
      }
    )
  }

  getSourcesDownload() {
    return this.authSubmit('get', `/download_sources`)
  }

  onRated() {
    return this.authSubmit('post', `/rating/rated`)
  }

  onRating(form) {
    return this.authSubmit('post', `/rating`, form)
  }

  getMessages(payload) {
    return this.authSubmit('get', 'tickets/messages/detail', null, {
      params: {
        page: payload.page || null,
        per_page: payload.perPage || null
      }
    })
  }

  getMessagesByUser(payload) {
    return this.authSubmit(
      'get',
      `admin/users/${payload.userId}/messages`,
      null,
      {
        params: {
          page: payload.page,
          per_page: payload.perPage
        }
      }
    )
  }

  getMessageByTicket(payload) {
    return this.authSubmit(
      'get',
      `/tickets/${payload.ticketId}/messages`,
      null,
      {
        params: {
          page: payload.page,
          per_page: payload.perPage
        }
      }
    )
  }

  postMessages(payload) {
    const formData = new FormData()
    if (payload.text) {
      formData.append('content', payload.text)
    }
    if (payload.attachment) {
      formData.append('attachment', payload.attachment)
    }
    return this.authSubmit('post', `tickets/send_message`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  postTicketMessage(payload) {
    return this.authSubmit(
      'post',
      `/tickets/${payload.ticketId}/send_message`,
      {
        content: payload.content || null,
        attachment_id: payload.attachmentId || null,
        reply_to: payload.replyTo || null
      }
    )
  }

  uploadAttachment(payload) {
    const formData = new FormData()
    formData.append('file', payload.file)
    return this.authSubmit('post', `/attachments/upload`, formData, {
      onUploadProgress: payload.progressCallback,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  uploadShapeFile(formData) {
    return this.authSubmit('post', `/v1/public/geojson/convert`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 30 * 60 * 1000
    })
  }

  uploadVectorFile(data) {
    return this.authSubmit(
      'post',
      `/workspaces/${data.form.id}/vectors/upload`,
      data.form.formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 30 * 60 * 1000,
        onUploadProgress: data.progressCallback,
        cancelToken: new CancelToken(data.cancelCallback)
      }
    )
  }

  getTickets() {
    return this.authSubmit('get', `/tickets`)
  }

  getTicketById(payload) {
    return this.authSubmit('get', `tickets/${payload.ticketId}`)
  }

  // getTicketTypes () {
  //   return this.authSubmit('get', `/tickets/types`)
  // }

  postTicket(payload) {
    const formData = new FormData()
    formData.append('type', payload.type)
    formData.append('title', payload.title)
    formData.append('content', payload.content)
    if (payload.data) {
      formData.append('data', payload.data)
    }
    if (payload.attachments) {
      payload.attachments.forEach((attachment, index) => {
        formData.append(`attachments[${index}]`, attachment)
      })
    }
    return this.authSubmit('post', '/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  deleteTicket(payload) {
    const { ticketId } = payload
    return this.authSubmit('delete', `tickets/${ticketId}`)
  }

  getTicketModel() {
    return this.authSubmit('get', '/models')
  }

  getAdmins() {
    return this.authSubmit('get', '/admin_users')
  }

  getTicketUsers() {
    return this.authSubmit('get', '/admin/tickets/users')
  }

  getAdminTickets(payload) {
    return this.authSubmit('get', `admin/tickets`, null, {
      params: {
        page: payload.page,
        per_page: payload.perPage,
        user_id: payload.userId || null
      }
    })
  }

  assignAdminToTicket(payload) {
    return this.authSubmit('post', '/admin/tickets/assign', {
      uid: payload.adminId,
      ticket_id: payload.ticketId
    })
  }

  changeTicketStatus(payload) {
    return this.authSubmit(
      'post',
      `/admin/tickets/${payload.ticketId}/change_status`,
      {
        status: payload.status
      }
    )
  }

  getUserPlans() {
    return this.authSubmit('get', `/userplans`)
  }

  getardToolkit() {
    return this.authSubmit('get', `/ard_rates`)
  }

  getPremise() {
    return this.authSubmit('get', `/userplans/on_premise`)
  }


  getUserProducts() {
    return this.authSubmit('get', `/products`)
  }

  updateUser(form) {
    return this.authSubmit('put', `/user`, form)
  }

  uploadModelThumbnail(payload) {
    const formData = new FormData()
    formData.append('file', payload)
    return this.authSubmit('post', `/file/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  uploadModelSampleImage(payload) {
    return this.authSubmit('post', `/files/upload`, payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  makeOrderModis(url) {
    return this.authSubmit('post', `/image_modis/make_order`, {
      order_url: url
    })
  }

  getProductModis() {
    return this.authSubmit('get', `/image_modis/products`)
  }

  getListOrderSkywatch(key) {
    return this.authSubmit('post', `/image_skywatch/orders`, {
      skywatch_key: key
    })
  }

  datasetDiscovery(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/datasets/discovery`,
      form.data
    )
  }

  downloadDataset(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/datasets/discovery/feature`,
      form.data,
      {
        timeout: 3 * 60 * 1000
      }
    )
  }

  getDatasetCategories() {
    return this.authSubmit('get', `/datasets/discovery/categories`)
  }

  getListDataset(id) {
    return this.authSubmit('get', `/workspaces/${id}/datasets`)
  }

  getDetailDataset(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/datasets/${form.id}`
    )
  }

  deleteDataset(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/datasets/${form.id}`
    )
  }

  updateDataset(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/datasets/${form.id}`,
      {
        annotation: form.annotation,
        bound: form.bound,
        image_id: form.imageId,
        name: form.name,
        categories: form.categories
      }
    )
  }

  searchSentinel1(form) {
    return this.authSubmit('post', `v1/imagery/discovery`, form, {
      timeout: 5 * 60 * 1000
    })
  }

  searchSTACLansatlook(form) {
    return axios.post(`https://landsatlook.usgs.gov/stac-server/search`, form)
  }

  downloadImage(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/upload?type=${form.type}`,
      form.data
    )
  }

  getImageValuesByLatlng(workspaceId, imageId, latlng) {
    let param = {
      lat: latlng.lat,
      lng: latlng.lng
    }
    return this.authSubmit(
      'get',
      `/v1/workspaces/${workspaceId}/imageries/${imageId}/inspect`,
      param
    )
  }

  getImageHistogram(workspaceId, imageId) {
    return this.authSubmit(
      'get',
      `/v1/workspaces/${workspaceId}/imageries/${imageId}/histogram`
    )
  }
  getTotalUniqueValue(ownerId, workspaceId, imageId, band) {
    return this.authSubmit(
      'get',
      `/v1/user/${ownerId}/workspaces/${workspaceId}/imageries/${imageId}/total_unique_value?band=${band}`
    )
  }
  formulas() {
    return this.authSubmit('get', `/formulas`)
  }

  deleteFormulas(id) {
    return this.authSubmit('delete', `/formulas/${id}`)
  }

  createFormulas(data) {
    return this.authSubmit('post', `/formulas`, data.data)
  }

  updateFormulas(data) {
    return this.authSubmit('put', `/formulas/${data.id}`, data.data)
  }

  getHistogram(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/results/${form.id}/histogram`
    )
  }

  getConfig() {
    return this.authSubmit('get', `/band_config`)
  }

  getBandList() {
    return this.authSubmit('get', `/bands`)
  }

  createNewImage(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/rgb_render`,
      form.data,
      {
        timeout: 5 * 60 * 1000
      }
    )
  }

  getEmails() {
    return this.authSubmit('get', `/users/contact`)
  }

  reClassification(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.resultId}/reclassification`,
      form.data
    )
  }

  removeResultFromShareWithMe(form) {
    return this.authSubmit('delete', `/results/${form.resultId}/shared`)
  }

  copyResultFromShareWithMe(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.data.workspace_id}/results/${form.resultId}/shared/make_copy`,
      {
        file_name: form.data.file_name
      }
    )
  }

  getShareResults(data) {
    let param = {
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      from_date: data.fromDate ? data.fromDate : '',
      to_date: data.toDate ? data.toDate : '',
      sort_by:
        data.sortBy && data.sortBy.length > 0 ? data.sortBy[0] : 'created_at',
      sort_desc: data.sortDesc ? data.sortDesc : false
    }
    return this.authSubmit('get', `/results/shared`, param)
  }

  /**
   * Create article to sharing in social network
   * @author Pv Duc
   * @param payload
   * @return Promise<any>
   */
  createArticle(payload) {
    const formData = new FormData()
    formData.append('title', payload.title)
    formData.append('content', payload.content)
    formData.append('image', payload.image)
    return this.authSubmit('post', '/social/post', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  /**
   * @author Pv Duc
   * @return Promise<any>
   */

  /**
   * Request http remove one imagery folder by id
   * @author Pv Duc
   * @param payload
   * @return Promise<any>
   */
  deleteImageryFolder(payload) {
    return this.authSubmit(
      'delete',
      `workspaces/${payload.workspaceId}/folders/${payload.folderId}/image`,
      null
    )
  }

  /**
   * Request http move imagery folder to another folder
   * @author Pv Duc
   * @param payload
   * @return Promise<any>
   */
  moveImageryFolder(payload) {
    return this.authSubmit(
      'post',
      `workspaces/${payload.workspaceId}/folders/move`,
      {
        folder_id: payload.folderId || null,
        parent_id: payload.parentId || null
      }
    )
  }

  /**
   * Request http remove one model folder by id
   * @author Pv Duc
   * @param payload
   * @return Promise<any>
   */
  deleteModelFolder(payload) {
    return this.authSubmit(
      'delete',
      `workspaces/${payload.workspaceId}/model_folders/${payload.folderId}/model`,
      null
    )
  }

  /**
   * Request http move model folder to another folder
   * @author Pv Duc
   * @param payload
   * @return Promise<any>
   */
  moveModelFolder(payload) {
    return this.authSubmit(
      'post',
      `workspaces/${payload.workspaceId}/model_folders/move`,
      {
        folder_id: payload.folderId || null,
        parent_id: payload.parentId || null
      }
    )
  }

  getActivity(payload) {
    return this.authSubmit('get', 'activitylog', null, {
      params: {
        workspace_id: payload.workspaceId,
        page: payload.page,
        per_page: payload.perPage,
        status: payload.status,
        from: payload.from || null,
        to: payload.to || null
      }
    })
  }

  getWorkspaceShareRoles() {
    return this.authSubmit('get', 'workspace_roles', null)
  }

  updateWorkspaceRole(payload) {
    return this.authSubmit(
      'put',
      `/workspaces/${payload.workspaceId}/shared/change_role`,
      {
        user_workspace_roles: payload.userWorkspaceRoles
      }
    )
  }

  removeWorkspaceSharedUser(payload) {
    return this.authSubmit(
      'delete',
      `/workspaces/${payload.workspaceId}/shared/user`,
      {
        email: payload.email
      }
    )
  }

  removeSharedWorkspace(payload) {
    return this.authSubmit('delete', 'workspaces/shared', {
      workspace_id: payload.workspaceId
    })
  }

  makeCopySharedImageFolder(payload) {
    return this.authSubmit(
      'post',
      `workspaces/${payload.workspaceId}/folders/shared/make_copy`,
      {
        folder_id: payload.folderId
      }
    )
  }

  makeCopySharedModelFolder(payload) {
    return this.authSubmit(
      'post',
      `workspaces/${payload.workspaceId}/model_folders/shared/make_copy`,
      {
        folder_id: payload.folderId
      }
    )
  }

  removeSharedModelFolder(payload) {
    return this.authSubmit('delete', 'model_folders/shared', {
      folder_ids: [payload.folderId]
    })
  }

  removeSharedImageFolder(payload) {
    return this.authSubmit('delete', 'folders/shared', {
      folder_ids: [payload.folderId]
    })
  }

  stackImage(payload) {
    return this.authSubmit(
      'post',
      `workspaces/${payload.workspaceId}/imageries/band_combination`,
      {
        image_ids: payload.imageIds || null,
        formula: payload.formula || null,
        folder_id: payload.folderId || null,
        name: payload.name || null
      }
    )
  }

  createDataset(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/datasets/training`,
      form.data
    )
  }

  zonalIndex(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/results/${form.resultId}/zonal`,
      form.data
    )
  }

  getAlgorithms() {
    return this.authSubmit('get', `results/algorithms`)
  }

  trainingV2(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/datasets/v2/training`,
      form.data
    )
  }


  getBBoxResult(form) {
    return this.authSubmit(
      'get',
      `workspaces/${form.workspaceId}/results/${form.resultId}/bbox`
    )
  }

  getStyle(data) {
    return this.authSubmit('get', `vectors/styles`, data)
  }

  getVector(data) {
    let param = {
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      order_by: data.order_by,
      order: data.order,
      search: data.search ? data.search : '',
      id: data.folderId
    }
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/vector_folders/v2`,
      param
    )
  }
  getDataFolderVector(data) {
    let param = {
      id: data.folderId
    }
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/vector_folders`,
      param
    )
  }

  getVectorShared(data) {
    let param = {
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      order_by: data.order_by,
      order: data.order,
      search: data.search ? data.search : '',
      id: data.folderId
    }
    return this.authSubmit('get', `/vectors/shared`, param)
  }

  deleteVector(form) {
    return this.authSubmit(
      'delete',
      `workspaces/${form.workspaceId}/vectors`,
      form.data
    )
  }

  removeSharedVector(data) {
    return this.authSubmit('delete', `/vectors/${data.id}/shared`)
  }

  updateVector(data) {
    return this.authSubmit(
      'put',
      `workspaces/${data.workspaceId}/vectors/${data.id}`,
      {
        name: data.name,
        style_id: data.styleId,
        categories: data.categories
      }
    )
  }

  setStyle(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/vectors/${form.vectorId}/style`,
      form.data
    )
  }

  makeCopyVector(data) {
    return this.authSubmit(
      'post',
      `/workspaces/${data.workspaceId}/vectors/shared/make_copy`,
      data.data
    )
  }

  getBandHistogram(data) {
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/imageries/${data.imageId}/stat?nodata=${data.nodata}`
    )
  }

  calculateStretchV2(data) {
    return this.authSubmit(
      'post',
      `tile/v2/minmax/${data.userId}/${data.workspaceId}/${data.imageId}`,
      data.data
    )
  }

  saveConfigImage(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/properties`,
      form.data
    )
  }

  markSeenMessage() {
    return this.authSubmit('put', `/tickets/messages/detail`)
  }

  getTrainingReport(taskId) {
    return this.authSubmit('get', `/tasks/${taskId}/training/report`)
  }

  getVectorFolders({ workspaceId, folderId }) {
    return this.authSubmit(
      'get',
      `/workspaces/${workspaceId}/vector_folders/v2`,
      {
        id: folderId
      }
    )
  }
  createVectorFolder({ workspaceId, name, folderId }) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/vector_folders`,
      {
        name,
        parent_id: folderId
      }
    )
  }
  renameVectorFolder({ workspaceId, folderId, name }) {
    return this.authSubmit(
      'put',
      `/workspaces/${workspaceId}/vector_folders/${folderId}`,
      {
        name
      }
    )
  }
  moveVectorFolder({ workspaceId, folderId, parentId }) {
    return this.authSubmit(
      'post',
      `workspaces/${workspaceId}/vector_folders/move`,
      {
        folder_id: folderId,
        parent_id: parentId
      }
    )
  }
  deleteVectorFolder({ workspaceId, folderId }) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/vector_folders/${folderId}`
    )
  }
  shareVectorFolder({ workspaceId, folderId, emails }) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/vector_folders/share`,
      {
        folder_id: folderId,
        emails
      }
    )
  }
  removeSharedVectorFolder({ folderId }) {
    return this.authSubmit('delete', '/vector_folders/shared', {
      folder_ids: [folderId]
    })
  }
  makeCopyVectorFolder({ workspaceId, folderId }) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/vector_folders/shared/make_copy`,
      {
        folder_id: folderId
      }
    )
  }
  removeSharedVectorFolderUser({ workspaceId, folderId, email }) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/vector_folders/share/user`,
      {
        folder_id: folderId,
        email
      }
    )
  }
  getAllRegion() {
    return this.authSubmit('get', `/regions`)
  }

  getIndustries() {
    return this.authSubmit('get', `/industries`)
  }

  getPrepare(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/prepare_mosaics`,
      form.data
    )
  }
  creatPrepare(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/prepare_mosaics`,
      form.data
    )
  }
  runMosaic(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/prepare_mosaics/${form.id}/execute`,
      {
        name: form.name
      }
    )
  }
  deletePrepare(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/prepare_mosaics/${form.id}`
    )
  }
  updateGeojson(form) {
    return this.authSubmit(
      'put',
      `/v1/workspaces/${form.workspaceId}/prepare_mosaics/${form.id}/cutline`,
      form.data
    )
  }
  sendAccurateReport(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/accurate_report`,
      form.data
    )
  }
  getReport(form) {
    return this.authSubmit(
      'get',
      `/workspaces/${form.workspaceId}/models/${form.id}/accurate_report`
    )
  }
  submitAccurateReport(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/models/${form.model_id}/accurate_report`,
      form.data
    )
  }
  deleteReport(form) {
    return this.authSubmit(
      'delete',
      `/workspaces/${form.workspaceId}/accurate_report/${form.id}`
    )
  }
  updatePrepare(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/prepare_mosaics/${form.id}`,
      form.data
    )
  }
  moveVectorToFolder({ workspaceId, folderId, vectorIds }) {
    return this.authSubmit('post', `workspaces/${workspaceId}/vectors/move`, {
      vector_ids: vectorIds,
      folder_id: folderId || null
    })
  }
  downloadVectorGeoJson(path) {
    return this.authSubmit(
      'get',
      `v1/public/geojson/convert_from_path?path=${path}`,
      {
        timeout: 60 * 60 * 1000
      }
    )
  }
  convertVector(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/results/${form.result_id}/convert_to_vector`,
      form.data
    )
  }
  getListVector(workspaceId) {
    return this.authSubmit('get', `workspaces/${workspaceId}/vectors`)
  }
  getGeometry(vector_id) {
    return this.authSubmit('get', `pg/vectors/${vector_id}/geometry`)
  }
  // geometryTool (form) {
  //   return this.authSubmit('post', `/workspaces/${form.workspaceId}/vectors/${form.data.key}`, form.data)
  // }
  // unsupervisedTool (form) {
  //   return this.authSubmit('post', `/workspaces/${form.workspaceId}/imageries/${form.data.key}`, form.data)
  // }
  // getConfigVector () {
  //   return this.authSubmit('get', `/geometry_toolkits`)
  // }
  saveTempleStyle(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/vectors/${form.vectorId}/tmp_style`,
      form.data
    )
  }
  setDefaultStyle(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/vectors/${form.vectorId}/default_style`,
      form.data
    )
  }
  getListStyle(workspaceId) {
    return this.authSubmit(
      'get',
      `/vectors/styles?workspace_id=${workspaceId}`
    )
  }
  updateStyle(form) {
    return this.authSubmit('put', `/vectors/styles/${form.id}`, form.data)
  }
  deleteStyle(form) {
    return this.authSubmit('delete', `/vectors/styles/${form.id}`)
  }
  searchFull({
    page,
    perPage,
    q,
    workspaceIds,
    userIds,
    categories,
    from,
    to,
    types
  }) {
    return this.authSubmit('get', 'search', {
      page,
      per_page: perPage,
      q,
      workspace_ids: workspaceIds,
      user_ids: userIds,
      from,
      to,
      types,
      categories
    })
  }
  getUser({ page, perPage, q }) {
    return this.authSubmit('get', 'users', {
      page,
      per_page: perPage,
      q
    })
  }
  getWorkspaceTags({ q }) {
    return this.authSubmit('get', 'workspace/categories', { q })
  }
  getToolConfig(tool) {
    return this.authSubmit('get', `/tools`, {
      group: tool
    })
  }
  getAllResult(form) {
    return this.authSubmit('get', `/workspaces/${form.workspaceId}/results/v2`)
  }
  getPropertyValue(form) {
    return this.authSubmit(
      'get',
      `/vectors/${form.id}/property_value`,
      form.data
    )
  }
  reClass(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/imageries/reclassification`,
      form.data
    )
  }
  getRecentWorkspace() {
    return this.authSubmit('get', `/workspaces/recent`)
  }
  getOpenWorkspace({ workspaceId }) {
    return this.authSubmit('get', `/workspaces/${workspaceId}`)
  }
  classifyReclass(form) {
    return this.authSubmit(
      'get',
      `/v1/user/${form.user_id}/workspaces/${form.workspace_id}/imageries/${form.image_id}/${form.type}`,
      form.data,
      {
        timeout: 3 * 60 * 1000
      }
    )
  }
  getAOIShareWithMe() {
    return this.authSubmit('get', `/aois/shared`)
  }
  makeCopyAOI(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wks_id}/aois/shared/make_copy`,
      form.data
    )
  }
  removeSharedAOI(form) {
    return this.authSubmit('delete', `aois/${form.aoi_id}/shared`)
  }
  createTaskDownload(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wks_id}/imageries/download`,
      form.data
    )
  }
  checkAvailableImage(data) {
    return this.authSubmit('post', '/imageries/convert_product_type', data, {
      timeout: 1000 * 60 * 10
    })
  }
  getTree(data) {
    return this.authSubmit('get', '/folder/breadcrumbs', data)
  }
  copyAOI(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wks_id}/aois/copy_to_workspace`,
      form.data
    )
  }
  createShareLink(data) {
    return this.authSubmit('post', `/maps`, data)
  }
  getShareLink() {
    return this.authSubmit('get', `/maps`)
  }
  getDetailShareLink(id) {
    return this.authSubmit('get', `/maps/${id}`)
  }
  removeShareLink(id) {
    return this.authSubmit('delete', `/maps/${id}`)
  }
  print(data) {
    return this.authSubmit('post', `/print`, data, {
      responseType: 'blob',
      timeout: 1000 * 60 * 60
    })
  }
  updateVectorWMS(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/vectors/${form.vectorId}`,
      form.data
    )
  }
  createNewVector(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/vectors`,
      form.data
    )
  }
  getLinkUpload(form) {
    return this.authSubmit('get', `/presigned_url`, form)
  }
  uploadImageTest(form, request) {
    return this.authSubmit('post', '/upload-image', form, {
      timeout: 6 * 60 * 60 * 1000,
      onUploadProgress: request.progressCallback,
      cancelToken: new CancelToken(request.cancelCallback)
    })
  }
  createTaskUpload(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wksId}/imageries/client_upload`,
      form.data
    )
  }
  createTaskGG(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wksId}/imageries/import_google_drive`,
      form.data
    )
  }
  getFeature(form) {
    return this.authSubmit(
      'post',
      `pg/vectors/${form.id}/features/geom`,
      form.data
    )
  }
  getFeatures(form) {
    return this.authSubmit(
      'post',
      `pg/vectors/${form.id}/features/geoms`,
      form.data
    )
  }
  // getFeatureGeom (form) {
  //   return this.authSubmit('get', `v1/vectors/${form.id}/features/${form.gid}`)
  // }
  getListFeature(form) {
    return this.authSubmit(
      'post',
      `/pg/vectors/${form.id}/features`,
      form.data
    )
  }
  updateProperties(form) {
    return this.authSubmit(
      'put',
      `/pg/vectors/${form.id}/properties`,
      form.data
    )
  }
  deleteProperties(form) {
    return this.authSubmit(
      'delete',
      `/pg/vectors/${form.id}/properties`,
      form.data
    )
  }
  updateFeature(form) {
    return this.authSubmit(
      'post',
      `/pg/vectors/${form.id}/change_features`,
      form.data,
      {
        timeout: 10 * 60 * 1000
      }
    )
  }
  filterMBTiles(form) {
    return this.authSubmit(
      'post',
      `/pg/vectors/${form.id}/features_count`,
      form.data
    )
  }
  getInfoFeature(form) {
    return this.authSubmit('post', `/pg/vectors/inspect`, form.data)
  }
  getListProperty(form) {
    return this.authSubmit(
      'get',
      `/pg/vectors/${form.vectorId}/properties/data_type`
    )
  }
  getTypeOfVector(form) {
    return this.authSubmit('get', `/pg/vectors/${form.id}/vector_type`)
  }
  // table
  getListTables(workspaceId) {
    return this.authSubmit('get', `workspaces/${workspaceId}/tables`)
  }

  copyTableTo(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.workspaceId}/tables/copy_to_workspace`,
      form.data
    )
  }

  getTable(data) {
    let param = {
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      order_by: data.order_by,
      order: data.order,
      search: data.search ? data.search : '',
      id: data.folderId
    }
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/table_folders/v2`,
      param
    )
  }
  getDataFolderTable(data) {
    let param = {
      id: data.folderId
    }
    return this.authSubmit(
      'get',
      `/workspaces/${data.workspaceId}/table_folders`,
      param
    )
  }

  getTableShared(data) {
    let param = {
      page: data.page ? data.page : '',
      per_page: data.itemsPerPage ? data.itemsPerPage : '',
      order_by: data.order_by,
      order: data.order,
      search: data.search ? data.search : '',
      id: data.folderId
    }
    return this.authSubmit('get', `/tables/shared`, param)
  }

  deleteTable(form) {
    return this.authSubmit(
      'delete',
      `workspaces/${form.workspaceId}/tables`,
      form.data
    )
  }

  removeSharedTable(data) {
    return this.authSubmit('delete', `/tables/${data.id}/shared`)
  }

  updateTable(data) {
    return this.authSubmit(
      'put',
      `workspaces/${data.workspaceId}/tables/${data.id}`,
      {
        name: data.name,
        categories: data.categories
      }
    )
  }
  makeCopyTable(data) {
    return this.authSubmit(
      'post',
      `/workspaces/${data.workspaceId}/tables/shared/make_copy`,
      data.data
    )
  }
  uploadTableFile(data) {
    return this.authSubmit(
      'post',
      `/workspaces/${data.form.id}/tables/upload`,
      data.form.formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 3 * 60 * 1000,
        onUploadProgress: data.progressCallback,
        cancelToken: new CancelToken(data.cancelCallback)
      }
    )
  }

  getTableFolders({ workspaceId, folderId }) {
    return this.authSubmit(
      'get',
      `/workspaces/${workspaceId}/table_folders/v2`,
      {
        id: folderId
      }
    )
  }
  createTableFolder({ workspaceId, name, folderId }) {
    return this.authSubmit('post', `/workspaces/${workspaceId}/table_folders`, {
      name,
      parent_id: folderId
    })
  }
  renameTableFolder({ workspaceId, folderId, name }) {
    return this.authSubmit(
      'put',
      `/workspaces/${workspaceId}/table_folders/${folderId}`,
      {
        name
      }
    )
  }
  moveTableFolder({ workspaceId, folderId, parentId }) {
    return this.authSubmit(
      'post',
      `workspaces/${workspaceId}/table_folders/move`,
      {
        folder_id: folderId,
        parent_id: parentId
      }
    )
  }
  deleteTableFolder({ workspaceId, folderId }) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/table_folders/${folderId}`
    )
  }
  shareTableFolder({ workspaceId, folderId, emails }) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/table_folders/share`,
      {
        folder_id: folderId,
        emails
      }
    )
  }
  removeSharedTableFolder({ folderId }) {
    return this.authSubmit('delete', '/table_folders/shared', {
      folder_ids: [folderId]
    })
  }
  makeCopyTableFolder({ workspaceId, folderId }) {
    return this.authSubmit(
      'post',
      `/workspaces/${workspaceId}/table_folders/shared/make_copy`,
      {
        folder_id: folderId
      }
    )
  }
  removeSharedTableFolderUser({ workspaceId, folderId, email }) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/table_folders/share/user`,
      {
        folder_id: folderId,
        email
      }
    )
  }
  moveTableToFolder({ workspaceId, folderId, tableIds }) {
    return this.authSubmit('post', `workspaces/${workspaceId}/tables/move`, {
      table_ids: tableIds,
      folder_id: folderId || null
    })
  }
  createTaskUploadTable(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wksId}/tables/upload`,
      form.data
    )
  }
  createTaskUploadVector(form) {
    return this.authSubmit(
      'post',
      `/workspaces/${form.wksId}/vectors/upload`,
      form.data
    )
  }
  classifyProperty(form) {
    return this.authSubmit(
      'get',
      `/pg/vectors/${form.id}/property_value`,
      form.data
    )
  }
  getTableData(form) {
    return this.authSubmit(
      'post',
      `/pg/tables/${form.id}/list_records`,
      form.data
    )
  }
  updateTableData(form) {
    return this.authSubmit('put', `/pg/tables/${form.id}/records`, form.data)
  }
  getHeader(id, type = 'tables') {
    return this.authSubmit('get', `/pg/${type}/${id}/records/data_type`)
  }
  addTableData(form) {
    return this.authSubmit('post', `/pg/tables/${form.id}/records`, form.data)
  }
  deleteRowData(form) {
    return this.authSubmit(
      'delete',
      `/pg/tables/${form.id}/records`,
      form.data
    )
  }
  getVectorTileInfo(url) {
    return this.authSubmit('get', url)
  }

  getVectorProperties(id) {
    return this.authSubmit('get', `/pg/vectors/${id}/properties`)
  }

  getListImageSymbologies(mode = null, image_id = null, workspace_id = null) {
    return this.authSubmit('get', `/imageries/symbologies`, {
      mode,
      image_id,
      workspace_id
    })
  }
  createNewImageSymbology(form) {
    return this.authSubmit(
      'post',
      `workspaces/${form.workspaceId}/imageries/${form.imageId}/symbology`,
      form.data
    )
  }
  updateImageSymbology(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/symbologies/${form.symbologyId}`,
      form.data
    )
  }
  setDefaultImageSymbology(form) {
    return this.authSubmit(
      'put',
      `/workspaces/${form.workspaceId}/imageries/${form.imageId}/default_symbology`,
      form.data
    )
  }

  deleteSymbology(form) {
    return this.authSubmit(
      'delete',
      `/imageries/symbologies/${form.symbologyId}?image_id=${form.imageId}`
    )
  }
  copySymbology(form) {
    return this.authSubmit(
      'post',
      `/imageries/symbologies/${form.symbologyId}/copy`,
      form.data
    )
  }
  addTableColumn(form) {
    return this.authSubmit(
      'post',
      `/pg/tables/${form.tableId}/columns`,
      form.data
    )
  }

  updateTableColumn(form) {
    return this.authSubmit(
      'put',
      `/pg/tables/${form.tableId}/columns`,
      form.data
    )
  }
  removeTableColumn(form) {
    return this.authSubmit(
      'delete',
      `/pg/tables/${form.tableId}/columns`,
      form.data
    )
  }
  addVectorColumn(form) {
    return this.authSubmit(
      'post',
      `/pg/vectors/${form.vectorId}/columns`,
      form.data
    )
  }

  updateVectorColumn(form) {
    return this.authSubmit(
      'put',
      `/pg/vectors/${form.vectorId}/columns`,
      form.data
    )
  }
  removeVectorColumn(form) {
    return this.authSubmit(
      'delete',
      `/pg/vectors/${form.vectorId}/columns`,
      form.data
    )
  }

  // project
  getProjectList(workspaceId, params) {
    return this.authSubmit(
      'get',
      `/workspaces/${workspaceId}/projects`,
      params
    )
  }
  createProject(workspaceId, data) {
    return this.authSubmit('post', `/workspaces/${workspaceId}/projects`, data)
  }
  updateProject(workspaceId, project_id, data) {
    return this.authSubmit(
      'put',
      `/workspaces/${workspaceId}/projects/${project_id}`,
      data
    )
  }
  deleteProject(workspaceId, project_id) {
    return this.authSubmit(
      'delete',
      `/workspaces/${workspaceId}/projects/${project_id}`
    )
  }
  // mosaic
  getAllMosaics(baseUrl, payload) {
    return axios.get(`${baseUrl}/data/mosaics`, { params: payload })
  }
  getMosaic(baseUrl, mosaic_id, payload) {
    return axios.get(`${baseUrl}/data/mosaics/${mosaic_id}/tile.json`, {
      params: payload
    })
  }

  // terrain
  getAllTerrain(worksapce_id, payload) {
    return this.authSubmit(
      'get',
      `/workspaces/${worksapce_id}/terrain-providers`,
      payload
    )
  }
  createTerrain(worksapce_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/terrain-providers`,
      payload
    )
  }
  updateTerrain(worksapce_id, terrain_id, payload) {
    return this.authSubmit(
      'put',
      `/workspaces/${worksapce_id}/terrain-providers/${terrain_id}`,
      payload
    )
  }
  deleteTerrain(worksapce_id, terrain_id, payload) {
    return this.authSubmit(
      'delete',
      `/workspaces/${worksapce_id}/terrain-providers/${terrain_id}`,
      payload
    )
  }

  getCesiumIonTerrain() {
    return this.authSubmit('get', '/cesium-ion-terrain')
  }

  // training v2
  getTrainingsV2(worksapce_id, payload) {
    return this.authSubmit(
      'get',
      `/workspaces/${worksapce_id}/trainings`,
      payload
    )
  }
  getTrainingV2(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'get',
      `/workspaces/${worksapce_id}/trainings/${training_id}`,
      payload
    )
  }
  createTrainingV2(worksapce_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings`,
      payload
    )
  }
  createDatasets(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings/${training_id}/datasets`,
      payload
    )
  }
  cutDatasetBySize(worksapce_id, training_id, dataset_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings/${training_id}/datasets/${dataset_id}/cut`,
      payload
    )
  }
  mergeDatasets(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings/${training_id}/merge`,
      payload
    )
  }
  trainModel(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings/${training_id}/train`,
      payload
    )
  }
  saveModel(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'post',
      `/workspaces/${worksapce_id}/trainings/${training_id}/save`,
      payload
    )
  }

  // container
  getContainer(params = {}) {
    return this.submit('get', '/containers', params)
  }

  saveContainer(data) {
    return this.submit('post', '/containers', data)
  }

  updateContainer(id, data) {
    return this.submit('put', `/containers/${id}`, data)
  }

  deleteContainer(id, data) {
    return this.submit('delete', `/containers/${id}`, data)
  }

  upgradeStorage(containerId, datas = {}) {
    return this.submit('post', `/containers/${containerId}/resource`, datas)
  }

  showdownCluster(containerId, datas = {}) {
    return this.submit(
      'post',
      `/containers/${containerId}/shutdown_cluster`,
      datas
    )
  }

  getHubTags(params = {}) {
    return this.submit('get', '/hub_tags', params)
  }

  updateParamsTraining(worksapce_id, training_id, payload) {
    return this.authSubmit(
      'put',
      `/workspaces/${worksapce_id}/trainings/${training_id}`,
      payload
    )
  }
  getCrs(params) {
    return this.authSubmit('get', '/crs', params)
  }

  // stac
  getListCollections(url) {
    return axios.get(`${url}/collections`)
  }
  getCollectionInfo(url, collectionId) {
    return axios.get(`${url}/collections/${collectionId}`)
  }
  getListItems(url, form) {
    return this.authSubmit('post', `${url}/search`, form, { setRegion: false })
  }
  getItem(url) {
    return this.authSubmit('get', url, undefined, { setRegion: false })
  }
  explorer(url, form) {
    return axios.post(`${url}/search`, form)
  }
  getTile(url, params = {}) {
    return axios.get(`${url}`)
  }

  // arcgis
  getArcgisCollections() {
    return this.authSubmit('GET', `${config.catalog_url}/api/collections?storage=2&page=1&perPage=1000`)
  }
  getArcgisItems(collectionId, params = {}) {
    params['collection'] = collectionId
    Object.keys(params).forEach(key => params[key] === undefined ? delete params[key] : {})
    const query = new URLSearchParams(params).toString()
    return this.authSubmit('GET', `${config.catalog_url}/api/items?${query}`)
  }

  downloadFileFromUrl(url) {
    return this.authSubmit(
      'GET',
      `${config.catalog_url}/api/proxy?${url}`,
      undefined,
      { setRegion: false }
    )
  }

  createFineTuning(workspaceId, data) {
    return this.authSubmit('post', `/workspaces/${workspaceId}/trainings/fine-tune`, data)
  }
}

export default new Api()



// WEBPACK FOOTER //
// ./src/api/Api.js